import {
  Button,
  ButtonColorEnum,
  ClockIcon,
  EditIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonSizeEnum,
  IconSizeEnum,
  PlusIcon,
  TrashDeleteIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { getDaysOfWeekFormatted } from '@/utils/date';

import { Divider } from '../Divider/Divider';

import { Action } from './components/Action/Action';
import { ScheduleItemForm } from './components/ScheduleItemForm/ScheduleItemForm';
import { ScheduleItemFormContainer } from './components/ScheduleItemFormContainer/ScheduleItemFormContainer';
import { TimeField } from './components/TimeField/TimeField';
import { useDailySchedulesForm } from './hooks/useDailySchedulesForm';
import { useScheduleItemForm } from './hooks/useScheduleItemForm';
import { DailySchedulesFormData, ScheduleFormData } from './types';

type DailySchedulesProps = {
  value?: DailySchedulesFormData;
  onChange: (value: DailySchedulesFormData) => void;
};

export function DailySchedules({ onChange, value }: DailySchedulesProps) {
  const { t } = useTranslation();
  const [openScheduleIndex, setOpenScheduleIndex] = useState<number | null>(null);
  const [isAddingSchedule, setIsAddingSchedule] = useState(false);

  const schedulesForm = useDailySchedulesForm();
  const newScheduleItemForm = useScheduleItemForm();

  const editScheduleItemForm = useScheduleItemForm();
  const {
    fields: schedules,
    remove: removeSchedule,
    append: appendSchedule,
    update: updateSchedule,
  } = useFieldArray({
    control: schedulesForm.control,
    name: 'schedules',
    rules: {
      required: t('El campo es obligatorio'),
    },
  });

  const handleRemoveSchedule = (index: number) => {
    removeSchedule(index);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  const handleAppendSchedule = (scheduleFormData: ScheduleFormData) => {
    appendSchedule(scheduleFormData);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  const handleUpdateSchedule = (index: number, scheduleFormData: ScheduleFormData) => {
    updateSchedule(index, scheduleFormData);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  useEffect(() => {
    schedulesForm.reset(value);
  }, [schedulesForm.reset, value]);

  const handleAppendNewSchedule = newScheduleItemForm.handleSubmit(data => {
    handleAppendSchedule({
      id: uuidv4(),
      ...data,
    });
    setIsAddingSchedule(false);
    newScheduleItemForm.reset();
  });

  const handleOpenSchedule = (index: number) => {
    setIsAddingSchedule(false);

    if (openScheduleIndex !== index) {
      const schedule = schedules[index];
      editScheduleItemForm.reset(schedule);
      setOpenScheduleIndex(index);
    } else {
      setOpenScheduleIndex(null);
    }
  };

  const editScheduleItemFormData = useWatch({
    control: editScheduleItemForm.control,
  });

  useEffect(() => {
    if (openScheduleIndex === null) {
      return;
    }

    const schedule = schedules[openScheduleIndex];
    handleUpdateSchedule(openScheduleIndex, {
      ...schedule,
      ...editScheduleItemFormData,
      id: schedule.id,
    });
  }, [editScheduleItemFormData, openScheduleIndex]);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-2">
        {schedules.map((schedule, index) => {
          const isEditingSchedule = openScheduleIndex === index;
          const handleOpen = () => {
            handleOpenSchedule(index);
          };
          const handleDelete = () => {
            handleRemoveSchedule(index);
          };

          return (
            <div key={index} className="w-full">
              <div className="flex w-full">
                <div className="flex w-fit">
                  <ClockIcon className="mr-1 mt-2" size={IconSizeEnum.SM} />
                </div>

                <button
                  className="flex w-full rounded-size-8 bg-[black]/[.02] p-1"
                  onClick={handleOpen}
                  type="button"
                >
                  <div className="flex w-1/2 flex-col items-start">
                    <TimeField label={t('Inicio')} time={schedule.startHour} />

                    <TimeField label={t('Fin')} time={schedule.endHour} />
                  </div>

                  <div className="flex w-1/2 items-start gap-1">
                    <Typography color={FontColorEnum.Medium20} weight={FontWeightEnum.Semibold}>
                      {t('Repetición') + ':'}
                    </Typography>
                    <Typography color={FontColorEnum.Medium20}>
                      {getDaysOfWeekFormatted(schedule.repetitionDayOfWeek)}
                    </Typography>
                  </div>
                </button>

                <div className="ml-1 flex gap-1">
                  <Action icon={EditIcon} onClick={handleOpen} />

                  <Action icon={TrashDeleteIcon} onClick={handleDelete} />
                </div>
              </div>

              {isEditingSchedule && (
                <ScheduleItemFormContainer>
                  <ScheduleItemForm
                    form={editScheduleItemForm}
                    startHourInputName="startHour"
                    endHourInputName="endHour"
                    repetitionDayOfWeek="repetitionDayOfWeek"
                  />
                </ScheduleItemFormContainer>
              )}
            </div>
          );
        })}
      </div>

      {isAddingSchedule && (
        <div>
          <Divider className="mt-4" />

          <ScheduleItemFormContainer>
            <div className="mb-3">
              <Typography color={FontColorEnum.Medium20} weight={FontWeightEnum.Bold}>
                {t('Nueva')}
              </Typography>
            </div>

            <ScheduleItemForm
              form={newScheduleItemForm}
              startHourInputName="startHour"
              endHourInputName="endHour"
              repetitionDayOfWeek="repetitionDayOfWeek"
            />

            <div className="flex w-full justify-end gap-1">
              <Button
                color={ButtonColorEnum.GHOST}
                onClick={() => {
                  setIsAddingSchedule(false);
                }}
              >
                <Typography weight={FontWeightEnum.Semibold}>{t('Cancelar')}</Typography>
              </Button>

              <Button color={ButtonColorEnum.DEFAULT_GHOST} onClick={handleAppendNewSchedule}>
                {<Typography weight={FontWeightEnum.Semibold}>{t('Crear')}</Typography>}
              </Button>
            </div>
          </ScheduleItemFormContainer>
        </div>
      )}

      {schedules.length > 0 && <Divider className="my-4" />}

      <div className="mt-2 flex items-center justify-end gap-size-2">
        <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Semibold}>
          {t('Añadir programación')}
        </Typography>

        <IconButton
          size={IconButtonSizeEnum.SM}
          onClick={() => {
            setOpenScheduleIndex(null);
            setIsAddingSchedule(true);
          }}
        >
          <PlusIcon size={IconSizeEnum.XS} />
        </IconButton>
      </div>
    </div>
  );
}
